import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

interface PartnerPageContext {
  partnerId: string;
  partnerName: string;
  partnerSlug: string;
}

/**
 * Sección de Talent Acquisition: mientras está montada, supportIA (burbuja del
 * FAB + drawer) pasa a modo Agente TA Metrics — mismo mecanismo que el Hub de
 * Partnerships con `partnerCtx`.
 */
/** Qué agente de TA atiende el bot del costado. */
export type TaAgentVariant = 'metrics' | 'intake';

interface TaPageContext {
  section: 'talent-acquisition';
  /**
   * Qué agente atiende el bot del costado en esta página:
   *  - 'metrics': el Agente TA Metrics sobre la base entera (sección de TA).
   *  - 'intake': el mismo agente pero SOLO con las búsquedas de quien pregunta
   *    (apartado de Hiring Manager Intake — Sol 29/09/2026).
   * El avatar es el mismo en las dos: es el asistente de Talent Acquisition.
   */
  variant: TaAgentVariant;
}

/**
 * Owned Media (OML, 08/10/2026): mientras el apartado está montado, supportIA
 * responde sobre la info del apartado (cuentas visibles + Inicio).
 */
interface OmlPageContext {
  section: 'owned-media';
}

interface PageContextValue {
  partnerCtx: PartnerPageContext | null;
  setPartnerCtx: (ctx: PartnerPageContext | null) => void;
  taCtx: TaPageContext | null;
  setTaCtx: (ctx: TaPageContext | null) => void;
  omlCtx: OmlPageContext | null;
  setOmlCtx: (ctx: OmlPageContext | null) => void;
}

const PageCtx = createContext<PageContextValue>({
  partnerCtx: null,
  setPartnerCtx: () => {},
  taCtx: null,
  setTaCtx: () => {},
  omlCtx: null,
  setOmlCtx: () => {},
});

export function PageContextProvider({ children }: { children: ReactNode }) {
  const [partnerCtx, setPartnerCtx] = useState<PartnerPageContext | null>(null);
  const [taCtx, setTaCtx] = useState<TaPageContext | null>(null);
  const [omlCtx, setOmlCtx] = useState<OmlPageContext | null>(null);
  return (
    <PageCtx.Provider value={{ partnerCtx, setPartnerCtx, taCtx, setTaCtx, omlCtx, setOmlCtx }}>
      {children}
    </PageCtx.Provider>
  );
}

export function usePageContext() {
  return useContext(PageCtx);
}

export function useRegisterPartnerContext(ctx: PartnerPageContext | null) {
  const { setPartnerCtx } = usePageContext();

  useEffect(() => {
    if (ctx?.partnerId) setPartnerCtx(ctx);
    return () => setPartnerCtx(null);
  }, [ctx?.partnerId]);
}

